"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link } from "@/components/ui/link"
import { Wordmark } from "@/components/ui/wordmark"
import { AuthSplitLayout } from "@/layouts/auth/auth-split-layout"
import { toFieldError } from "@/lib/utils"
import { signInPath, signUpPath } from "@/routes"

// See sessions/new.tsx for detailed explanation of the Form Pattern.
// TL;DR: Uncontrolled inputs + FormData = copy-paste friendly UUI code.

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type PageProps = {
  errors: Record<string, string>
}

export default function Register() {
  const { errors } = usePage<PageProps>().props
  const [processing, setProcessing] = useState(false)

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.post(
      signUpPath(),
      Object.fromEntries(new FormData(e.currentTarget)),
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AuthSplitLayout>
      <Head title="Sign up" />
      <div className="flex flex-col items-center justify-center gap-6 text-center">
        <Link href="/" variant="muted" className="hover:opacity-80">
          <Wordmark size={30} className="text-primary" />
        </Link>
        <div className="flex flex-col gap-2 md:gap-3">
          <h1 className="text-display-xs md:text-display-sm text-primary">
            Create an account
          </h1>
          <p className="text-md text-tertiary">Start your 30-day free trial.</p>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          <Input
            label="Name"
            type="text"
            name="name"
            placeholder="Enter your name"
            size="md"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.name}
            error={toFieldError(errors?.name)}
            autoFocus
            autoComplete="name"
          />
          <Input
            label="Email"
            type="email"
            name="email"
            placeholder="Enter your email"
            size="md"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.email}
            error={toFieldError(errors?.email)}
            autoComplete="email"
          />
          <Input
            label="Password"
            type="password"
            name="password"
            size="md"
            placeholder="Create a password"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.password}
            error={toFieldError(errors?.password)}
            autoComplete="new-password"
            hint="Must be at least 8 characters"
          />
          <Input
            label="Confirm Password"
            type="password"
            name="password_confirmation"
            size="md"
            placeholder="Confirm your password"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.password_confirmation}
            error={toFieldError(errors?.password_confirmation)}
            autoComplete="new-password"
          />
        </div>

        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            size="lg"
            isLoading={processing}
            showTextWhileLoading
          >
            Get started
          </Button>
        </div>
      </form>

      <div className="flex justify-center gap-1 text-center">
        <span className="text-tertiary text-sm">Already have an account?</span>
        <Link
          href={signInPath()}
          className="text-brand-secondary hover:text-brand-secondary_hover text-sm font-semibold"
        >
          Log in
        </Link>
      </div>
    </AuthSplitLayout>
  )
}
